import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import * as CryptoJS from 'crypto-js';

export interface CryptoConfig {
    algorithm?: string;
    keySize?: number;
    iterations?: number;
}

@Injectable({
    providedIn: 'root',
})
export class CryptoService {
    private readonly defaultKey: string;
    private readonly salt: string;

    // Configuration for future encryption method changes
    private config: CryptoConfig;

    // Key derivation cache to avoid repeated PBKDF2 computations (10,000 iterations each)
    private readonly derivedKeyCache = new Map<string, { key: string; timestamp: number; }>();
    private readonly KEY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    constructor() {
        // Get keys from environment configuration
        this.defaultKey =
            environment.security?.encryptionKey ||
            'as51das1d5as1d5as1d35ad1s35d1as3d513s5a1das35d1sad53s13d1as321d';
        this.salt =
            environment.security?.encryptionSalt ||
            'asd165asdas65d1sd31f6ff1sdfsd1df3f1add16sa1das35d1asd15as3d1a1d';

        // Get encryption config from environment
        this.config = {
            algorithm: environment.security?.encryption?.algorithm || 'AES',
            keySize: environment.security?.encryption?.keySize || 256,
            iterations: environment.security?.encryption?.iterations || 10000,
        };

        // In production, validate that proper keys are set
        if (environment.production) {
            this.validateSecurityConfig();
        }

        this.initializeEncryption();
    }

    /**
     * Validate security configuration
     */
    private validateSecurityConfig(): void {
        if (
            this.defaultKey === 'as51das1d5as1d5as1d35ad1s35d1as3d513s5a1das35d1sad53s13d1as321d' ||
            this.defaultKey.includes('CHANGE_THIS')
        ) {
            throw new Error('CRITICAL: Encryption key not properly configured for production!');
        }

        if (
            this.salt === 'asd165asdas65d1sd31f6ff1sdfsd1df3f1add16sa1das35d1asd15as3d1a1d' ||
            this.salt.includes('CHANGE_THIS')
        ) {
            throw new Error('CRITICAL: Encryption salt not properly configured for production!');
        }

        if (this.defaultKey.length < 32) {
            throw new Error('CRITICAL: Encryption key too short!');
        }
    }

    /**
     * Initialize encryption with proper key derivation
     * This method makes it easy to switch to different encryption libraries
     */
    private initializeEncryption(): void {
        // Future: Can be replaced with WebCrypto API or other libraries
    }

    /**
     * Encrypt data using AES encryption
     * @param data - The data to encrypt (can be string, object, etc.)
     * @param customKey - Optional custom encryption key
     * @returns Encrypted string
     */
    encrypt(data: any, customKey?: string): string {
        try {
            const key = customKey || this.defaultKey;
            const dataString = typeof data === 'string' ? data : JSON.stringify(data);

            // Using CryptoJS for now - can be easily replaced
            const encrypted = CryptoJS.AES.encrypt(dataString, this.deriveKey(key));

            // Add integrity check
            const hmac = this.generateHMAC(encrypted.toString(), key);

            // Combine encrypted data with HMAC for integrity verification
            const combined = {
                data: encrypted.toString(),
                hmac: hmac,
                timestamp: Date.now(),
                version: '1.0', // Version for future migration support
            };

            // Base64 encode the final payload
            return this.base64Encode(JSON.stringify(combined));
        } catch {
            throw new Error('Failed to encrypt data');
        }
    }

    /**
     * Decrypt data
     * @param encryptedData - The encrypted string to decrypt
     * @param customKey - Optional custom decryption key
     * @returns Decrypted data
     */
    decrypt(encryptedData: string, customKey?: string): any {
        try {
            const key = customKey || this.defaultKey;

            // Decode from base64
            const decodedData = this.base64Decode(encryptedData);
            const combined = JSON.parse(decodedData);

            // Verify integrity
            if (!this.verifyHMAC(combined.data, combined.hmac, key)) {
                throw new Error('Data integrity check failed');
            }

            // Decrypt the data
            const decrypted = CryptoJS.AES.decrypt(combined.data, this.deriveKey(key));
            const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

            // Try to parse as JSON, otherwise return as string
            try {
                return JSON.parse(decryptedString);
            } catch {
                return decryptedString;
            }
        } catch {
            throw new Error('Failed to decrypt data');
        }
    }

    /**
     * Compact encrypt — AES only, no HMAC/timestamp/version wrapper and no extra
     * Base64 layer. Produces a much shorter token than encrypt() while staying
     * truly unreadable (random salt per call). Use where size matters and the
     * integrity wrapper isn't needed, e.g. URL query state.
     * @param data - The data to encrypt (string or object)
     * @param customKey - Optional custom encryption key
     * @returns AES ciphertext string (OpenSSL Base64 format)
     */
    encryptCompact(data: any, customKey?: string): string {
        const key = customKey || this.defaultKey;
        const dataString = typeof data === 'string' ? data : JSON.stringify(data);
        return CryptoJS.AES.encrypt(dataString, this.deriveKey(key)).toString();
    }

    /**
     * Decrypt a token produced by encryptCompact().
     * @param encryptedData - The compact ciphertext to decrypt
     * @param customKey - Optional custom decryption key
     * @returns Decrypted data (parsed JSON when possible, else the raw string)
     */
    decryptCompact(encryptedData: string, customKey?: string): any {
        const key = customKey || this.defaultKey;
        const decryptedString = CryptoJS.AES.decrypt(encryptedData, this.deriveKey(key)).toString(
            CryptoJS.enc.Utf8
        );

        if (!decryptedString) {
            throw new Error('Failed to decrypt data');
        }

        try {
            return JSON.parse(decryptedString);
        } catch {
            return decryptedString;
        }
    }

    /**
     * Derive a key from password using PBKDF2 with caching
     * Caches derived keys to avoid expensive PBKDF2 computation on every call
     * This makes the encryption more secure and allows for key rotation
     */
    private deriveKey(password: string): string {
        const cached = this.derivedKeyCache.get(password);

        // Return cached key if valid (within TTL)
        if (cached && Date.now() - cached.timestamp < this.KEY_CACHE_TTL) {
            return cached.key;
        }

        // Using PBKDF2 for key derivation (expensive operation - 10,000 iterations)
        const key = CryptoJS.PBKDF2(password, this.salt, {
            keySize: this.config.keySize! / 32,
            iterations: this.config.iterations!,
        });
        const derivedKey = key.toString();

        // Cache the derived key
        this.derivedKeyCache.set(password, {
            key: derivedKey,
            timestamp: Date.now(),
        });

        return derivedKey;
    }

    /**
     * Clear the key derivation cache
     * Should be called on logout to prevent token reuse
     */
    clearKeyCache(): void {
        this.derivedKeyCache.clear();
    }

    /**
     * Generate HMAC for data integrity
     */
    private generateHMAC(data: string, key: string): string {
        return CryptoJS.HmacSHA256(data, this.deriveKey(key)).toString();
    }

    /**
     * Verify HMAC for data integrity
     */
    private verifyHMAC(data: string, hmac: string, key: string): boolean {
        const calculatedHmac = this.generateHMAC(data, key);
        return calculatedHmac === hmac;
    }

    /**
     * Base64 encode
     */
    private base64Encode(data: string): string {
        return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(data));
    }

    /**
     * Base64 decode
     */
    private base64Decode(data: string): string {
        return CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(data));
    }

    /**
     * Generate a random encryption key
     * Useful for generating session-specific keys
     */
    generateRandomKey(length = 32): string {
        return CryptoJS.lib.WordArray.random(length).toString();
    }

    /**
     * Hash data using SHA-256
     * Useful for creating non-reversible identifiers
     */
    hash(data: string): string {
        return CryptoJS.SHA256(data).toString();
    }

    /**
     * Check if data is encrypted by this service
     */
    isEncrypted(data: string): boolean {
        try {
            const decoded = this.base64Decode(data);
            const parsed: any = JSON.parse(decoded);
            return (
                Object.hasOwn(parsed, 'data') &&
                Object.hasOwn(parsed, 'hmac') &&
                Object.hasOwn(parsed, 'version')
            );
        } catch {
            return false;
        }
    }

    /**
     * Get encryption strength info
     * Useful for security audits
     */
    getEncryptionInfo(): CryptoConfig & { provider: string; } {
        return {
            ...this.config,
            provider: 'CryptoJS', // Current provider - will change when upgrading
        };
    }

    /**
     * Future migration method template
     * When switching to a different encryption library, implement this
     */
    async migrateEncryption(
        oldEncryptedData: string,
        oldKey: string,
        _newMethod: string
    ): Promise<string> {
        // Decrypt with old method
        const decryptedData = this.decrypt(oldEncryptedData, oldKey);

        // Re-encrypt with new method
        // This is where you'd implement the new encryption logic

        // For now, just re-encrypt with current method
        return this.encrypt(decryptedData, oldKey);
    }
}
